import { motion } from "framer-motion";
import { HeartHandshake, Moon, Hammer, Users, ArrowRight, Mail } from "lucide-react";
import { Link } from "react-router-dom";
import SiteNav from "@/components/SiteNav";
import SiteFooter from "@/components/SiteFooter";
import Seo from "@/components/Seo";

const EASE = [0.16, 1, 0.3, 1];
const AMBER = "#f59e0b";

const fade = (delay = 0) => ({
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.7, delay, ease: EASE },
});

const CHAPTERS = [
  {
    Icon: HeartHandshake,
    kicker: "The day job",
    title: "Care work pays for the city",
    body: "By day, Tanner Rozankovic works as a disability support worker — helping people with their daily lives, their goals, and their independence. It is honest work, and it is also the entire budget of this game. Every wage that comes in funds what gets built at night: servers, tools, art, engine time, and the long hours of engineering that don't show up on an invoice.",
  },
  {
    Icon: Moon,
    kicker: "The night shift",
    title: "Self-funded means nobody else's clock",
    body: "There is no publisher, no investor, and no board behind New Citizen. As of today the project is funded in its entirety by one person's paycheck — which means the roadmap answers to the people who play, not to a quarterly report. Progress moves at the speed of real life, and that is a trade we make on purpose: slower, but honest.",
  },
  {
    Icon: Hammer,
    kicker: "Early access, honestly",
    title: "You are watching it being built",
    body: "New Citizen is in early development. Systems are unfinished, edges are rough, and things will break — because everything you see is being actively built around you. Early citizens aren't customers of a finished product; they are the founding residents of a city under construction, and their feedback shapes what gets built next.",
  },
  {
    Icon: Users,
    kicker: "Why it matters",
    title: "A different way to make a game",
    body: "Most games this ambitious start with a studio fund. This one started with a support worker who wanted a living city badly enough to pay for it himself. If you're here at the beginning, you're part of that story — and the city remembers its first citizens.",
  },
];

export default function FounderStoryPage() {
  return (
    <div data-testid="founder-story" className="min-h-screen bg-[#07080a] text-zinc-100 flex flex-col">
      <Seo
        title="The Founder Story — Self-Funded, One Shift at a Time | Specpixel Entertainment"
        description="New Citizen is entirely self-funded by Tanner Rozankovic, who finances development from his wages as a disability support worker. The honest story behind the city."
      />
      <SiteNav />

      {/* hero */}
      <section className="relative overflow-hidden border-b border-white/10">
        <div className="absolute inset-0 opacity-[0.06] pointer-events-none" style={{ backgroundImage: "repeating-linear-gradient(0deg, #f59e0b 0 1px, transparent 1px 90px)" }} />
        <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(60% 50% at 50% 100%, rgba(245,158,11,0.12), transparent 70%)" }} />
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 pt-24 pb-20 text-center">
          <motion.p {...fade(0)} className="font-mono text-[10px] tracking-[0.5em] uppercase" style={{ color: AMBER }}>
            The founder story
          </motion.p>
          <motion.h1 {...fade(0.1)} className="font-sans font-black uppercase tracking-tight text-4xl sm:text-6xl mt-5 leading-[1.05]">
            Built one shift<br />at a time
          </motion.h1>
          <motion.p {...fade(0.2)} className="font-mono text-xs sm:text-sm text-zinc-400 leading-relaxed max-w-2xl mx-auto mt-7">
            New Citizen is an early-development roleplay world from Specpixel Entertainment — and as of today it is
            entirely self-funded by Tanner Rozankovic, who pays for its development out of his own wages earned
            working as a disability support worker.
          </motion.p>
        </div>
      </section>

      {/* pull quote */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 py-16">
        <motion.blockquote {...fade(0)} className="border-l-2 pl-6 sm:pl-8 py-2" style={{ borderColor: AMBER }}>
          <p className="font-sans font-bold text-2xl sm:text-3xl leading-snug text-zinc-100">
            "Every street in this city was paid for one shift at a time."
          </p>
          <footer className="font-mono text-[10px] tracking-[0.3em] text-zinc-500 uppercase mt-4">— Tanner Rozankovic, Founder</footer>
        </motion.blockquote>
      </section>

      {/* chapters */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 pb-20 flex flex-col gap-6">
        {CHAPTERS.map((c, i) => (
          <motion.article key={c.title} {...fade(i * 0.05)} data-testid={`story-chapter-${i}`}
            className="border border-white/10 bg-white/[0.02] p-7 sm:p-9 relative overflow-hidden">
            <div className="absolute top-0 left-0 right-0 h-[2px]" style={{ background: `linear-gradient(90deg, ${AMBER}, transparent)` }} />
            <div className="flex items-center gap-3 mb-4">
              <c.Icon className="w-4 h-4" style={{ color: AMBER }} />
              <p className="font-mono text-[9px] tracking-[0.4em] uppercase" style={{ color: AMBER }}>{c.kicker}</p>
            </div>
            <h2 className="font-sans font-bold uppercase tracking-wide text-xl sm:text-2xl text-zinc-50 mb-4">{c.title}</h2>
            <p className="font-sans text-sm sm:text-base leading-relaxed text-zinc-300">{c.body}</p>
          </motion.article>
        ))}
      </section>

      {/* press + cta */}
      <section className="border-t border-white/10 bg-black/40">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-16 flex flex-col md:flex-row md:items-center gap-8 justify-between">
          <div>
            <p className="font-mono text-[9px] tracking-[0.4em] uppercase text-zinc-500 mb-2">Press & enquiries</p>
            <a data-testid="story-press-mail" href="mailto:business@specpixel.com"
              className="font-mono text-sm text-zinc-200 hover:text-white transition-colors inline-flex items-center gap-2">
              <Mail className="w-4 h-4" style={{ color: AMBER }} /> business@specpixel.com
            </a>
            <p className="font-mono text-[10px] text-zinc-500 mt-3 leading-relaxed max-w-md">
              Interviews, founder Q&A, and early-access coverage are welcome — the whole point of building in the open is being able to talk about it.
            </p>
          </div>
          <div className="flex gap-3 flex-wrap">
            <Link to="/roadmap" data-testid="story-roadmap-cta"
              className="px-7 py-3.5 font-sans font-semibold uppercase tracking-[0.2em] text-xs text-black inline-flex items-center gap-2 transition-transform hover:scale-[1.03]"
              style={{ backgroundColor: AMBER, boxShadow: "0 0 24px rgba(245,158,11,0.25)" }}>
              See the roadmap <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/new-citizen/servers" data-testid="story-play-cta"
              className="px-7 py-3.5 font-sans font-semibold uppercase tracking-[0.2em] text-xs border border-white/20 hover:border-white/50 text-zinc-200 inline-flex items-center gap-2 transition-colors">
              Join the city
            </Link>
          </div>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}
